import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotations as api, alertSettings } from '../utils/api';
import { STATUS_LIST, STATUS_META, ORDER_STATUS_LIST, ORDER_STATUS_META, ORDER_STATUS_PT, fmtBRL } from '../utils/helpers';
import { useToast, ToastContainer } from '../hooks/useToast';
import { useT, useI18n } from '../i18n/I18nContext';

const ALERT_THRESHOLD_KEY = {
  'Ready to Send':    { override: 'alert_ready_days',        setting: 'ready_days' },
  'Sent':             { override: 'alert_sent_days',         setting: 'sent_days' },
  'Response Received':{ override: 'alert_waiting_days',      setting: 'waiting_days' },
  'Under Analysis':   { override: 'alert_analysis_days',     setting: 'analysis_days' },
  'In Negotiation':   { override: 'alert_negotiation_days',  setting: 'negotiation_days' },
};

function calcDeadline(quotation, settings) {
  const keys = ALERT_THRESHOLD_KEY[quotation.status];
  if (!keys || !quotation.status_changed_at) return null;
  const threshold = quotation[keys.override] || (settings && settings[keys.setting]) || null;
  if (!threshold) return null;
  const since = new Date(quotation.status_changed_at);
  const deadline = new Date(since.getTime() + threshold * 24 * 60 * 60 * 1000);
  return deadline;
}

function DeadlineBadge({ quotation, settings }) {
  const t = useT();
  const { lang } = useI18n();
  const deadline = calcDeadline(quotation, settings);
  if (!deadline) return null;

  const now = new Date();
  const diffMs = deadline - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  let bg, color, label;
  if (diffDays < 0) {
    bg = '#fde8e8'; color = '#c0392b';
    label = t('kanban.deadline.overdue').replace('{n}', Math.abs(diffDays));
  } else if (diffDays <= 2) {
    bg = '#fff3e0'; color = '#e65100';
    label = t('kanban.deadline.due_in').replace('{n}', diffDays);
  } else {
    bg = '#e8f5e9'; color = '#2e7d32';
    const dateStr = deadline.toLocaleDateString(lang === 'pt' ? 'pt-BR' : 'en-US');
    label = t('kanban.deadline.due_date').replace('{date}', dateStr);
  }

  return (
    <div style={{
      marginTop: 8,
      fontSize: 11,
      fontWeight: 600,
      padding: '3px 8px',
      borderRadius: 4,
      background: bg,
      color,
      display: 'inline-block',
    }}>
      ⏱ {label}
    </div>
  );
}

export default function Kanban() {
  const t = useT();
  const [allQuotations, setAllQuotations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toasts, toast } = useToast();
  const navigate = useNavigate();

  const load = useCallback(() => {
    Promise.all([api.list({ limit: 9999 }), alertSettings.get()])
      .then(([data, cfg]) => { setAllQuotations(data?.rows || data); setSettings(cfg); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  async function moveStatus(id, status) {
    try {
      await api.setStatus(id, status);
      toast(t('kanban.status.updated').replace('{s}', status), 'success');
      load();
    } catch (err) {
      toast(err.response?.data?.error || t('kanban.error.move'), 'error');
      load();
    }
  }

  const [dragOverQuotation, setDragOverQuotation] = useState(null);
  const [dragOverOrder, setDragOverOrder] = useState(null);

  const quotationItems = allQuotations.filter(q => !q.order_type || q.order_type === 'quotation');
  const orderItems = allQuotations.filter(q => q.order_type && q.order_type !== 'quotation');

  const byQuotationStatus = STATUS_LIST.reduce((acc, s) => {
    acc[s] = quotationItems.filter(q => q.status === s);
    return acc;
  }, {});

  const byOrderStatus = ORDER_STATUS_LIST.reduce((acc, s) => {
    acc[s] = orderItems.filter(q => q.status === s);
    return acc;
  }, {});

  const orphanOrders = orderItems.filter(q => !ORDER_STATUS_LIST.includes(q.status));

  if (loading) return <div className="page"><div className="text-muted">{t('common.loading')}</div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">🗂️ {t('kanban.title')}</div>
          <div className="page-subtitle">{quotationItems.length} {t('kanban.section.quotations').toLowerCase()} · {orderItems.length} {t('kanban.section.orders').toLowerCase()}</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/quotations?new=1')}>{t('kanban.new')}</button>
      </div>

      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-400)', letterSpacing: 1, marginBottom: 12, textTransform: 'uppercase' }}>
          📋 {t('kanban.section.quotations')}
        </div>
        <div className="kanban-board">
          {STATUS_LIST.map(status => {
            const cards = byQuotationStatus[status] || [];
            const meta = STATUS_META[status];
            const isOver = dragOverQuotation === status;
            return (
              <div
                key={status}
                className="kanban-col"
                onDragOver={e => { e.preventDefault(); setDragOverQuotation(status); }}
                onDragLeave={e => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setDragOverQuotation(null);
                  }
                }}
                onDrop={e => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData('quotation_id');
                  const sourceStatus = e.dataTransfer.getData('source_status');
                  const cardOrderType = e.dataTransfer.getData('order_type');
                  setDragOverQuotation(null);
                  if (id && sourceStatus !== status && !['sample','bulk'].includes(cardOrderType)) {
                    moveStatus(id, status);
                  }
                }}
                style={isOver ? { background: 'var(--accent)', outline: '2px dashed var(--pantone)', borderRadius: 'var(--radius)' } : {}}
              >
                <div className="kanban-col-header">
                  <span className="kanban-col-title">{meta.emoji} {status}</span>
                  <span className="kanban-col-count">{cards.length}</span>
                </div>
                {cards.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'20px 0', color:'var(--gray-300)', fontSize:12 }}>{t('kanban.empty_col')}</div>
                ) : (
                  cards.map(q => (
                    <KanbanCard key={q.id} quotation={q} settings={settings}
                      currentStatus={status}
                      onOpen={() => navigate(`/quotations/${q.id}`)}
                      onMove={newStatus => moveStatus(q.id, newStatus)} />
                  ))
                )}
              </div>
            );
          })}
        </div>
      </div>

      {orphanOrders.length > 0 && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#FFF3E0', borderRadius: 'var(--radius)', border: '1px solid #FFB74D' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#E65100', marginBottom: 8 }}>
            ⚠️ {orphanOrders.length} {t('kanban.items_plural')} {t('kanban.error.move')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {orphanOrders.map(q => (
              <div key={q.id}
                style={{ padding: '4px 10px', background: 'white', borderRadius: 6, border: '1px solid #FFB74D', fontSize: 12, cursor: 'pointer', color: 'var(--pantone)', fontWeight: 600 }}
                onClick={() => navigate(`/orders/${q.id}`)}>
                {q.reference} ({q.supplier_name}) — status: {q.status}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-400)', letterSpacing: 1, marginBottom: 12, textTransform: 'uppercase' }}>
          📦 {t('kanban.section.orders')}
        </div>
        {orderItems.length === 0 ? (
          <div style={{ color: 'var(--gray-300)', fontSize: 13, padding: 16 }}>
            {t('kanban.orders.empty')}
          </div>
        ) : (
          <div className="kanban-board">
            {ORDER_STATUS_LIST.map(status => {
              const cards = byOrderStatus[status] || [];
              const meta = ORDER_STATUS_META[status];
              const isOver = dragOverOrder === status;
              return (
                <div
                  key={status}
                  className="kanban-col"
                  onDragOver={e => { e.preventDefault(); setDragOverOrder(status); }}
                  onDragLeave={e => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      setDragOverOrder(null);
                    }
                  }}
                  onDrop={e => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData('quotation_id');
                    const sourceStatus = e.dataTransfer.getData('source_status');
                    const cardOrderType = e.dataTransfer.getData('order_type');
                    setDragOverOrder(null);
                    if (id && sourceStatus !== status && ['sample','bulk'].includes(cardOrderType)) {
                      moveStatus(id, status);
                    }
                  }}
                  style={isOver ? { background: 'var(--accent)', outline: '2px dashed var(--pantone)', borderRadius: 'var(--radius)' } : {}}
                >
                  <div className="kanban-col-header">
                    <span className="kanban-col-title">{meta.emoji} {ORDER_STATUS_PT[status]||status}</span>
                    <span className="kanban-col-count">{cards.length}</span>
                  </div>
                  {cards.length === 0 ? (
                    <div style={{ textAlign:'center', padding:'20px 0', color:'var(--gray-300)', fontSize:12 }}>{t('kanban.empty_col')}</div>
                  ) : (
                    cards.map(q => (
                      <KanbanCard key={q.id} quotation={q} settings={settings}
                        currentStatus={status}
                        onOpen={() => navigate(`/orders/${q.id}`)}
                        onMove={newStatus => moveStatus(q.id, newStatus)}
                        orderType={q.order_type} />
                    ))
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} />
    </div>
  );
}

function KanbanCard({ quotation: q, settings, currentStatus, onOpen, onMove, orderType }) {
  const t = useT();
  const [showMenu, setShowMenu] = useState(false);
  const statusList = orderType && orderType !== 'quotation' ? ORDER_STATUS_LIST : STATUS_LIST;
  const nextStatuses = statusList.filter(s => s !== currentStatus);

  const itemsLabel = q.item_count === 1
    ? `1 ${t('kanban.items_singular')}`
    : `${q.item_count} ${t('kanban.items_plural')}`;

  return (
    <div
      className="kanban-card"
      draggable
      onDragStart={e => {
        e.dataTransfer.setData('quotation_id', q.id);
        e.dataTransfer.setData('source_status', q.status);
        e.dataTransfer.setData('order_type', q.order_type || '');
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={onOpen}
      // Quando o menu "Move to" está aberto, elevamos o card acima dos irmãos —
      // senão o :hover (transform) do card de baixo cria um stacking context que
      // sobe por cima do menu e causa o efeito de "piscar".
      style={{ cursor: 'grab', ...(showMenu ? { position: 'relative', zIndex: 50 } : {}) }}
    >
      <div className="kanban-card-ref">{q.reference}</div>
      <div className="kanban-card-supplier">{q.supplier_name}</div>
      {orderType && (
        <div style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
          background: orderType === 'sample' ? '#FFF3E0' : '#E3F2FD',
          color: orderType === 'sample' ? '#E65100' : '#1565C0',
          marginBottom: 4,
        }}>
          {orderType === 'sample' ? '🟠 Sample' : '🔵 Bulk'}
        </div>
      )}
      <div className="kanban-card-collection">
        {q.collection && <span className="tag">{q.collection}</span>}
        {q.supplier_country && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--gray-400)' }}>{q.supplier_country}</span>}
      </div>
      <DeadlineBadge quotation={q} settings={settings} />
      <div className="kanban-card-footer">
        <span className="kanban-card-items">📦 {itemsLabel}</span>
        <span className="kanban-card-value">{q.total_estimated_brl > 0 ? fmtBRL(q.total_estimated_brl) : '—'}</span>
      </div>
      <div style={{ marginTop: 10, position: 'relative' }} onClick={e => e.stopPropagation()}>
        <button
          className="btn btn-ghost btn-sm w-full"
          style={{ fontSize: 11, justifyContent: 'center' }}
          onClick={() => setShowMenu(!showMenu)}
        >
          {t('kanban.move_to')}
        </button>
        {showMenu && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
            background: 'white', border: '1px solid var(--gray-200)',
            borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)',
            overflow: 'hidden', marginTop: 4,
          }}>
            {nextStatuses.map(s => (
              <div
                key={s}
                style={{ padding: '9px 14px', fontSize: 12, cursor: 'pointer', transition: 'background .1s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
                onClick={() => { onMove(s); setShowMenu(false); }}
              >
                {(orderType && orderType !== 'quotation' ? ORDER_STATUS_META : STATUS_META)[s]?.emoji} {s}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
